<script setup>

import {computed, onMounted, ref} from "vue";

const currentQuotesPerMonth = ref(20);
const currentCloseRate = ref(30);
const averageTimeToClose = ref(14);
const averageDealValue = ref(10000);
const expectedCloseRateImprovement = ref(15);
const expectedTimeReduction = ref(5);

const transitioned = ref(false)
const calculating = ref(false);
const seeResult = ref(false);

const results = computed(() => {
    // Current performance
    const currentDealsClosed = currentQuotesPerMonth.value * (currentCloseRate.value / 100);

    // New performance with digital quotes
    const newCloseRate = currentCloseRate.value + expectedCloseRateImprovement.value;
    const newDealsClosed = currentQuotesPerMonth.value * (newCloseRate / 100);

    // Additional deals closed per month
    const additionalDeals = newDealsClosed - currentDealsClosed;

    // Additional revenue per month
    const additionalRevenuePerMonth = additionalDeals * averageDealValue.value;

    // Time saved per month (days)
    // Time saved = reduction in days * number of deals that close faster
    const newTimeToClose = Math.max(1, averageTimeToClose.value - expectedTimeReduction.value);
    const timeSavedPerDeal = averageTimeToClose.value - newTimeToClose;
    const timeSavedPerMonth = timeSavedPerDeal * newDealsClosed;

    // Revenue increase per year
    const revenueIncreasePerYear = additionalRevenuePerMonth * 12;

    // Cost of delayed closes (opportunity cost)
    // This represents the cost of waiting longer to close deals
    // Simplified: cost = (time difference / 30) * monthly revenue opportunity
    const monthlyRevenueOpportunity = currentDealsClosed * averageDealValue.value;
    const costOfDelayedCloses = (timeSavedPerDeal / 30) * monthlyRevenueOpportunity;

    return {
        additionalDeals: additionalDeals.toLocaleString('en-US', {maximumFractionDigits:1}).replaceAll(',', ' '),
        additionalDealsRaw: additionalDeals,
        additionalRevenuePerMonth: additionalRevenuePerMonth.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        additionalRevenuePerMonthRaw: additionalRevenuePerMonth,
        timeSavedPerMonth: timeSavedPerMonth.toLocaleString('en-US', {maximumFractionDigits:1}).replaceAll(',', ' '),
        timeSavedPerMonthRaw: timeSavedPerMonth,
        revenueIncreasePerYear: revenueIncreasePerYear.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        revenueIncreasePerYearRaw: revenueIncreasePerYear,
        costOfDelayedCloses: costOfDelayedCloses.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        costOfDelayedClosesRaw: costOfDelayedCloses,
    };
})

const calculateInitial = () => {
    calculating.value = true

    setTimeout(function(){
        seeResult.value = true;

        setTimeout(function() {
            window.scrollBy({
                top: 500,
                behavior: 'smooth'
            });
        }, 300);
    }, 300)
}

onMounted(() => {
    setTimeout(() => {
        transitioned.value = true;
    }, 100);
})

</script>

<template>
    <div>
        <card-basic
            class="transition-all duration-500"
            :class="transitioned ? 'translate-y-0 scale-100' : '-translate-y-4 opacity-0 scale-95'"
        >
            <div class="grid-cols-2 gap-8 md:grid">
                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Current quotes sent per month
                    </p>
                    <input-number
                        max="10000"
                        min="0"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="currentQuotesPerMonth"
                        :show-step-ui="true"
                        placeholder="Quotes per month"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Current close rate (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="currentCloseRate"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="currentCloseRate = $event"
                        />{{ currentCloseRate }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average time to close (days)
                    </p>
                    <input-number
                        max="365"
                        min="1"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="averageTimeToClose"
                        :show-step-ui="true"
                        placeholder="Days"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average deal value
                    </p>
                    <input-number
                        max="1000000"
                        min="0"
                        :step="100"
                        class="mx-auto w-36"
                        v-model:content="averageDealValue"
                        :show-step-ui="true"
                        placeholder="Deal value"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Expected improvement in close rate with digital quotes (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="expectedCloseRateImprovement"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="expectedCloseRateImprovement = $event"
                        />{{ expectedCloseRateImprovement }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Expected reduction in time to close (days)
                    </p>
                    <input-number
                        max="365"
                        min="0"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="expectedTimeReduction"
                        :show-step-ui="true"
                        placeholder="Days reduction"
                    />
                </div>

                <div
                    v-if="! seeResult"
                    class="flex justify-center col-span-2"
                >
                    <button-primary
                        class="justify-center w-48 mx-auto transition-all"
                        :class="calculating ? 'translate-y-8 opacity-0' : ''"
                        @click="calculateInitial"
                    >
                        Calculate
                    </button-primary>
                </div>

                <div
                    v-if="calculating"
                    class="col-span-2 -mx-4 -mt-6 -mb-4 transition-colors duration-1000 text-wlPrimaryContrast md:-mx-6 md:-mb-6 lg:-mx-8 lg:-mb-8"
                    :class="seeResult ? 'bg-wlPrimary' : 'bg-transparent'"
                >
                    <div class="w-full h-12 transform -translate-y-px bg-white rounded-b-wl"></div>
                    <div
                        class="col-span-2 p-4 pt-3 transition-all duration-1000 rounded-b-wl md:p-6 md:pt-4 lg:p-8 lg:pt-5"
                        :class="seeResult ? 'translate-y-0 opacity-100 max-h-screen' : '-translate-y-8 opacity-0 max-h-0'"
                    >
                        <div class="flex mb-8 space-x-4">
                            <div>
                                <i class="mt-1 text-2xl fa-regular fa-chart-line"></i>
                            </div>
                            <div>
                                <p>With digital quotes, you would close <strong>{{ results.additionalDeals }} additional deals per month</strong>.</p>

                                <p>This would generate <strong>${{ results.additionalRevenuePerMonth }}</strong> in additional revenue per month.</p>

                                <p>You would save <strong>{{ results.timeSavedPerMonth }} days per month</strong> in time to close.</p>

                                <p>Revenue increase per year: <strong>${{ results.revenueIncreasePerYear }}</strong></p>

                                <p class="mt-4">
                                    <strong>Cost of delayed closes (opportunity cost): ${{ results.costOfDelayedCloses }}</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card-basic>
    </div>
</template>

<style scoped>

</style>

